import {
  memo,
  useRef,
  useCallback,
  useLayoutEffect,
  useImperativeHandle,
  forwardRef,
  ForwardedRef,
  useState,
} from 'react';
import type { Game } from '@betty-gaming/daily-wheel';

import { BettyWheelProps, BettyWheelExposedProps } from './BettyWheel.types';
import { Box, Loader } from '@betty/moonlight';
import { logError } from '@betty/toolkit/logging';

const WHEEL_WIDTH = 320;
const WHEEL_HEIGHT = 320;

const BettyWheel = (
  { handleWin, getSectors, getReward }: BettyWheelProps,
  ref: ForwardedRef<BettyWheelExposedProps>,
) => {
  const [isReady, setIsReady] = useState(false);
  const wheelInstance = useRef<Game | undefined>();
  const dailyWheelElementRef = useRef<HTMLDivElement | null>(null);

  const initWheel = useCallback(async () => {
    const [BettyWheelLib, sectors] = await Promise.all([
      import('@betty-gaming/daily-wheel'),
      getSectors(),
    ]);

    wheelInstance.current = new BettyWheelLib.Game({
      selectorId: `betty-wheel`,
      width: WHEEL_WIDTH,
      height: WHEEL_HEIGHT,
      wheelDiameter: 302,
      hideClaimButton: true,
      debug: false,
      coinAmount: 100,
      maxFps: 60,
      versionHash: `${Math.random()}`, // TODO change this
      sectors,
      onFinish: handleWin,
      onRequestError: (error) => logError('Wheel error', error),
      onReady: () => setIsReady(true),
      getReward,
    });

    wheelInstance.current?.run();
  }, [getSectors, handleWin, getReward]);

  // On mount, initialize the Daily Wheel
  useLayoutEffect(() => {
    if (wheelInstance.current) {
      wheelInstance.current?.run();
    } else {
      initWheel();
    }

    return () => {
      wheelInstance.current?.destroy();
      wheelInstance.current = undefined;
    };
  }, [initWheel]);

  useImperativeHandle(
    ref,
    () => ({
      reset: () => wheelInstance.current?.reset(),
    }),
    [],
  );

  return (
    <Box centerContent>
      <div
        style={{ width: WHEEL_WIDTH, height: WHEEL_HEIGHT }}
        className="betty-wheel"
        id="betty-wheel"
        ref={dailyWheelElementRef}
      />
      {isReady ? null : (
        <Box absoluteFill centerContent>
          <Loader />
        </Box>
      )}
    </Box>
  );
};

export default memo(forwardRef(BettyWheel));
